import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { jordanGovernorates } from '../data/jordanGovernorates';

const { FiMapPin, FiUsers, FiInfo } = FiIcons;

const JordanMap = ({ experts, onExpertSelect }) => {
  const [hoveredGov, setHoveredGov] = useState(null);
  
  // Convert visual percentages to SVG coordinates
  const getVisualPos = (vx, vy) => ({
    x: (vx / 100) * 500,
    y: (vy / 100) * 600
  });

  const approvedExperts = experts.filter(e => e.status === 'approved');

  return (
    <div className="relative w-full h-full bg-slate-50 rounded-xl overflow-hidden border border-slate-200 shadow-inner">
      {/* Legend */}
      <div className="absolute top-4 left-4 z-10 pointer-events-none">
        <div className="bg-white/80 backdrop-blur-sm p-3 rounded-lg border border-slate-100 shadow-sm">
          <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Network Map</h4>
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-1.5">
              <div className="w-2 h-2 rounded-full bg-blue-600"></div>
              <span className="text-[10px] font-medium text-slate-600">City</span>
            </div>
            <div className="flex items-center space-x-1.5">
              <div className="w-2 h-2 rounded-full bg-green-500"></div>
              <span className="text-[10px] font-medium text-slate-600">Expert</span>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full h-full flex items-center justify-center p-4">
        <svg 
          viewBox="0 0 500 600" 
          className="w-full h-full max-h-[480px] drop-shadow-xl"
        >
          {/* Background Connections (Subtle Network Lines) */}
          <path 
            d="M240,50 L360,90 L350,230 L240,250 L100,210 Z" 
            fill="none" 
            stroke="#e2e8f0" 
            strokeWidth="1" 
            strokeDasharray="4 4"
          />

          {/* Governorate Regions */}
          {jordanGovernorates.map((gov) => {
            const { x, y } = getVisualPos(gov.visualX, gov.visualY);
            const isHovered = hoveredGov === gov.name;
            const govExperts = approvedExperts.filter(e => e.governorate === gov.name);

            return (
              <g 
                key={gov.name}
                onMouseEnter={() => setHoveredGov(gov.name)}
                onMouseLeave={() => setHoveredGov(null)}
                className="cursor-pointer group"
              >
                {/* Visual Area for the Governorate */}
                <motion.circle 
                  cx={x} 
                  cy={y} 
                  r={isHovered ? 40 : 32} 
                  initial={false}
                  animate={{ 
                    r: isHovered ? 42 : 32,
                    fill: isHovered ? '#f0fdf4' : '#ffffff',
                    stroke: isHovered ? '#4ade80' : '#e2e8f0'
                  }}
                  className="stroke-2 transition-colors duration-300"
                />
                
                {/* City Center Marker */}
                <circle 
                  cx={x} 
                  cy={y} 
                  r="3.5" 
                  className="fill-blue-600" 
                />
                
                {/* City Label */}
                <text 
                  x={x} 
                  y={y + 18} 
                  textAnchor="middle" 
                  className={`text-[11px] font-bold tracking-tight transition-colors duration-300 ${
                    isHovered ? 'fill-green-700' : 'fill-slate-400'
                  }`}
                >
                  {gov.name}
                </text>

                {/* Expert Count Badge (only if > 0) */}
                {govExperts.length > 0 && (
                  <g transform={`translate(${x + 18}, ${y - 18})`}>
                    <circle r="9" className="fill-green-600 shadow-sm" />
                    <text 
                      textAnchor="middle" 
                      dy="3.5" 
                      className="fill-white text-[10px] font-black"
                    >
                      {govExperts.length}
                    </text>
                  </g>
                )}

                {/* Small Expert Dots distributed around the center if many */}
                {govExperts.map((exp, idx) => {
                  // Distribute expert dots in a circle around the city center
                  const angle = (idx / govExperts.length) * Math.PI * 2;
                  const radius = 22;
                  const dotX = x + Math.cos(angle) * radius;
                  const dotY = y + Math.sin(angle) * radius;

                  return (
                    <motion.circle
                      key={exp.id}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      whileHover={{ scale: 1.5, fill: '#16a34a' }}
                      cx={dotX}
                      cy={dotY}
                      r="5"
                      onClick={(e) => {
                        e.stopPropagation();
                        onExpertSelect(exp);
                      }}
                      className="fill-green-500 stroke-white stroke-[1.5px] drop-shadow-sm"
                    />
                  );
                })}
              </g>
            );
          })}
        </svg>
      </div>

      {/* Info Overlay */}
      <AnimatePresence>
        {hoveredGov && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-white px-5 py-2.5 rounded-2xl shadow-2xl border border-slate-100 flex items-center space-x-4 min-w-[200px] justify-center"
          >
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiMapPin} className="text-blue-600" />
              </div>
              <div>
                <div className="font-bold text-slate-800 text-sm">{hoveredGov}</div>
                <div className="text-[10px] text-slate-500 font-medium tracking-wide">GOVERNORATE</div>
              </div>
            </div>
            <div className="h-8 w-px bg-slate-100"></div>
            <div className="text-center">
              <div className="font-black text-green-600 text-sm">
                {approvedExperts.filter(e => e.governorate === hoveredGov).length}
              </div>
              <div className="text-[10px] text-slate-500 font-medium">EXPERTS</div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="absolute bottom-4 right-4 text-[9px] font-bold text-slate-400 uppercase tracking-[0.2em] pointer-events-none">
        Jordan Vector Grid v1.0
      </div>
    </div>
  );
};

export default JordanMap;